import { useState } from "react";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import {
  ESTILOS_DE_FONTE,
  FONTES_DE_TEXTO,
  MAX_CARACTERES_POR_LINHA,
  MAX_LINHAS_DO_TEXTO,
  ROTULO_DO_ESTILO_DE_FONTE,
  caracteresSemGarantia,
  estiloCssDaFonte,
  fonteDeTexto,
  pesosDaFonte,
  temItalico,
  type Camada,
  type CamadaDeTexto,
  type FonteDeTexto,
} from "@foto/shared";
import { Area, Chave, Segmentos, Selecao, cx } from "../../components/ui.js";
import {
  definirItalico,
  definirRotacao,
  definirTamanhoDaCamada,
  limitarTextoDigitado,
  medidasDoTexto,
  pesoEfetivo,
  rotuloDoPeso,
  tamanhoDaCamada,
  trocarFonteDoTexto,
} from "./camadas-da-celula.js";
import { Deslizante, Secao, SeletorDeCor } from "./controles.js";

const ALINHAMENTOS = [
  { id: "esquerda", rotulo: "Esquerda" },
  { id: "centro", rotulo: "Centro" },
  { id: "direita", rotulo: "Direita" },
] as const;

/** Quanto do contorno o controle deslizante mostra: a espessura é fração do corpo (0 a 0,25), e aqui vira 0 a 25. */
const CONTORNO_PADRAO = { cor: "#1a1a1a", espessura: 0.08 };

/**
 * O que se ajusta na camada selecionada. Toda camada tem tamanho e giro; o texto tem, além disso, o campo,
 * a fonte, o peso, o itálico, o alinhamento, a cor, o contorno e a entrelinha.
 *
 * `aoAlterar` recebe a camada nova e, nos controles que se arrastam ou se digitam, a **chave** do agrupamento
 * do histórico (um gesto = uma entrada do desfazer). `aoSoltar` fecha o agrupamento.
 */
export function InspetorDaCamada({
  camada,
  aoAlterar,
  aoSoltar,
}: {
  camada: Camada;
  aoAlterar: (camada: Camada, chave?: string) => void;
  aoSoltar: () => void;
}) {
  const tamanho = (
    <Deslizante
      rotulo={camada.tipo === "imagem" ? "Tamanho" : "Tamanho da letra"}
      valor={tamanhoDaCamada(camada)}
      min={camada.tipo === "imagem" ? 5 : 2}
      max={camada.tipo === "imagem" ? 150 : 40}
      passo={camada.tipo === "imagem" ? 1 : 0.5}
      formato={(v) => (camada.tipo === "imagem" ? `${Math.round(v)}% da foto` : `${v}`)}
      aoMudar={(v) => aoAlterar(definirTamanhoDaCamada(camada, v), `tamanho:${camada.id}`)}
      aoSoltar={aoSoltar}
    />
  );
  const giro = (
    <div>
      <Deslizante
        rotulo="Giro"
        valor={camada.rotacao}
        min={-180}
        max={180}
        passo={1}
        formato={(v) => `${Math.round(v)}°`}
        aoMudar={(v) => aoAlterar(definirRotacao(camada, v), `giro:${camada.id}`)}
        aoSoltar={aoSoltar}
      />
    </div>
  );

  if (camada.tipo === "imagem") {
    return (
      <Secao titulo="Ajustes da imagem">
        {tamanho}
        {giro}
      </Secao>
    );
  }
  return (
    <InspetorDoTexto
      camada={camada}
      tamanho={tamanho}
      giro={giro}
      aoAlterar={aoAlterar}
      aoSoltar={aoSoltar}
    />
  );
}

function InspetorDoTexto({
  camada,
  tamanho,
  giro,
  aoAlterar,
  aoSoltar,
}: {
  camada: CamadaDeTexto;
  tamanho: React.ReactNode;
  giro: React.ReactNode;
  aoAlterar: (camada: Camada, chave?: string) => void;
  aoSoltar: () => void;
}) {
  const fonte = fonteDeTexto(camada.fonteId);
  const medidas = medidasDoTexto(camada.texto);
  const semGarantia = caracteresSemGarantia(camada.texto);
  const [cortou, setCortou] = useState(false);
  const emBranco = camada.texto.trim() === "";

  return (
    <Secao titulo="Ajustes do texto">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="campo-do-texto" className="text-[0.8125rem] font-medium text-texto-suave">
          Texto
        </label>
        <Area
          id="campo-do-texto"
          rows={3}
          value={camada.texto}
          spellCheck
          autoComplete="off"
          onChange={(e) => {
            const r = limitarTextoDigitado(e.target.value);
            setCortou(r.cortado);
            aoAlterar({ ...camada, texto: r.texto }, `texto:${camada.id}`);
          }}
          onBlur={aoSoltar}
          aria-describedby="medidas-do-texto"
        />
        <p id="medidas-do-texto" className="text-[0.75rem] leading-relaxed text-texto-fraco">
          {medidas.linhas} {medidas.linhas === 1 ? "linha" : "linhas"} (até {MAX_LINHAS_DO_TEXTO}) ·
          a maior tem {medidas.maiorLinha} de {MAX_CARACTERES_POR_LINHA} letras. Enter quebra a
          linha.
        </p>
        {cortou && (
          <p role="status" className="text-[0.75rem] leading-relaxed text-descartar">
            O limite é de {MAX_LINHAS_DO_TEXTO} linhas de {MAX_CARACTERES_POR_LINHA} letras: o que
            passou foi cortado.
          </p>
        )}
        {emBranco && (
          <p role="status" className="text-[0.75rem] leading-relaxed text-descartar">
            Texto em branco não vai na folha: escreva algo, ou apague esta camada.
          </p>
        )}
        {semGarantia.length > 0 && (
          <p role="status" className="text-[0.75rem] leading-relaxed text-descartar">
            Estes caracteres podem não sair na impressão (as fontes não têm todos):{" "}
            <span className="font-medium">{semGarantia.join(" ")}</span>
          </p>
        )}
      </div>

      <SeletorDeFonte
        camada={camada}
        aoEscolher={(f) => aoAlterar(trocarFonteDoTexto(camada, f))}
      />

      {fonte && <PesoEItalico camada={camada} fonte={fonte} aoAlterar={aoAlterar} />}

      <Segmentos
        rotulo="Alinhamento das linhas"
        valor={camada.alinhamento}
        opcoes={ALINHAMENTOS}
        aoMudar={(alinhamento) => aoAlterar({ ...camada, alinhamento })}
      />

      <SeletorDeCor
        rotulo="Cor da letra"
        valor={camada.cor}
        aoMudar={(cor) => aoAlterar({ ...camada, cor }, `cor:${camada.id}`)}
        aoSoltar={aoSoltar}
      />

      <div className="flex flex-col gap-1">
        <Chave
          ligada={camada.contorno !== undefined}
          rotulo="Contorno da letra"
          texto="Contorno"
          aoAlternar={(ligado) => {
            const { contorno: anterior, ...resto } = camada;
            aoAlterar(ligado ? { ...resto, contorno: anterior ?? CONTORNO_PADRAO } : resto);
          }}
        />
        <p className="text-[0.75rem] leading-relaxed text-texto-fraco">
          O contorno ajuda a ler o texto sobre uma foto clara ou cheia de detalhes.
        </p>
      </div>
      {camada.contorno && (
        <div className="flex flex-col gap-3 rounded-peca border border-borda p-3">
          <SeletorDeCor
            rotulo="Cor do contorno"
            valor={camada.contorno.cor}
            aoMudar={(cor) =>
              aoAlterar(
                { ...camada, contorno: { ...(camada.contorno ?? CONTORNO_PADRAO), cor } },
                `contorno-cor:${camada.id}`,
              )
            }
            aoSoltar={aoSoltar}
          />
          <Deslizante
            rotulo="Espessura do contorno"
            valor={Math.round(camada.contorno.espessura * 100)}
            min={1}
            max={25}
            passo={1}
            aoMudar={(v) =>
              aoAlterar(
                {
                  ...camada,
                  contorno: { ...(camada.contorno ?? CONTORNO_PADRAO), espessura: v / 100 },
                },
                `contorno-espessura:${camada.id}`,
              )
            }
            aoSoltar={aoSoltar}
          />
        </div>
      )}

      {tamanho}
      <Deslizante
        rotulo="Entrelinha"
        valor={camada.entrelinha}
        min={0.8}
        max={2}
        passo={0.05}
        formato={(v) => `${v.toFixed(2).replace(".", ",")}×`}
        aoMudar={(v) => aoAlterar({ ...camada, entrelinha: v }, `entrelinha:${camada.id}`)}
        aoSoltar={aoSoltar}
      />
      {giro}
    </Secao>
  );
}

/** O peso (só se a fonte tem mais de um) e o itálico (só se a fonte tem). Controle que não muda nada não aparece. */
function PesoEItalico({
  camada,
  fonte,
  aoAlterar,
}: {
  camada: CamadaDeTexto;
  fonte: FonteDeTexto;
  aoAlterar: (camada: Camada, chave?: string) => void;
}) {
  const pesos = pesosDaFonte(fonte, camada.italico);
  const efetivo = pesoEfetivo(fonte, camada.peso, camada.italico);
  const comItalico = temItalico(fonte);
  if (pesos.valores.length <= 1 && !comItalico) return null;

  return (
    <div className="flex flex-col gap-2">
      {pesos.valores.length > 1 && (
        <label className="block">
          <span className="mb-1.5 block text-[0.8125rem] font-medium text-texto-suave">Peso</span>
          <Selecao
            value={efetivo}
            className="min-h-11"
            onChange={(e) => aoAlterar({ ...camada, peso: Number(e.target.value) })}
          >
            {pesos.valores.map((p) => (
              <option key={p} value={p}>
                {rotuloDoPeso(p)}
              </option>
            ))}
          </Selecao>
        </label>
      )}
      {comItalico && (
        <Chave
          ligada={camada.italico}
          rotulo="Itálico"
          texto="Itálico"
          aoAlternar={(ligado) => aoAlterar(definirItalico(camada, ligado))}
        />
      )}
    </div>
  );
}

/**
 * A fonte, com as 13 do pacote mostradas **na própria fonte**, agrupadas por estilo. A lista só abre sob
 * demanda: cada nome pede o arquivo da sua fonte (até 1,3 MB, o da Festive), e baixar as 13 para quem só quer
 * trocar a cor seria desperdício. Os arquivos ficam no cache do navegador depois da primeira vez.
 */
function SeletorDeFonte({
  camada,
  aoEscolher,
}: {
  camada: CamadaDeTexto;
  aoEscolher: (fonte: FonteDeTexto) => void;
}) {
  const [aberto, setAberto] = useState(false);
  const atual = fonteDeTexto(camada.fonteId);

  return (
    <div>
      <span className="mb-1.5 block text-[0.8125rem] font-medium text-texto-suave">Fonte</span>
      <button
        type="button"
        aria-expanded={aberto}
        aria-controls="lista-de-fontes"
        onClick={() => setAberto((a) => !a)}
        className="flex min-h-11 w-full items-center justify-between gap-3 rounded-peca border border-borda bg-superficie px-3 text-left text-texto transition-colors hover:border-borda-forte"
      >
        <span
          className="truncate text-[1.125rem]"
          style={atual ? estiloDaFonteNoSeletor(atual, camada) : undefined}
        >
          {atual?.nome ?? "Escolha uma fonte"}
        </span>
        {aberto ? <CaretUp size={16} aria-hidden /> : <CaretDown size={16} aria-hidden />}
      </button>

      {aberto && (
        <div
          id="lista-de-fontes"
          className="mt-2 max-h-72 overflow-y-auto rounded-peca border border-borda bg-superficie"
        >
          {ESTILOS_DE_FONTE.map((estilo) => {
            const fontes = FONTES_DE_TEXTO.filter((f) => f.estilo === estilo);
            if (fontes.length === 0) return null;
            return (
              <div key={estilo} role="group" aria-label={ROTULO_DO_ESTILO_DE_FONTE[estilo]}>
                <p className="sticky top-0 bg-superficie-alta px-3 py-1.5 text-[0.75rem] font-medium uppercase tracking-wide text-texto-fraco">
                  {ROTULO_DO_ESTILO_DE_FONTE[estilo]}
                </p>
                {fontes.map((f) => {
                  const escolhida = f.id === camada.fonteId;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      aria-pressed={escolhida}
                      onClick={() => {
                        aoEscolher(f);
                        setAberto(false);
                      }}
                      className={cx(
                        "flex min-h-12 w-full items-center px-3 text-left text-[1.25rem] leading-tight transition-colors",
                        escolhida
                          ? "bg-brasa-fraca text-brasa"
                          : "text-texto hover:bg-superficie-alta",
                      )}
                      style={estiloCssDaFonte(f, f.pesoPadrao, false)}
                    >
                      {f.nome}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** O nome da fonte atual, escrito com o peso e o itálico que a camada tem. */
function estiloDaFonteNoSeletor(fonte: FonteDeTexto, camada: CamadaDeTexto) {
  return estiloCssDaFonte(fonte, camada.peso, camada.italico);
}
